import React, { ComponentType, useMemo } from 'react';

import { NiatApplicationMachineProvider } from '../context/NiatApplicationMachineProvider';
import {
  NiatApplicationMachineReturnType,
  useNiatApplicationMachine
} from '../hooks/useNiatApplicationMachine';
import {
  NiatApplicationMachineSendType,
  NiatApplicationMachineStateType,
  NiatPortalGraphQlApiStatusType
} from '../types/niatApplicationMachineTypes';

export interface WithNiatApplicationPortalMachineProps {
  niatApplicationMachineState: NiatApplicationMachineStateType;
  niatApplicationMachineSend: NiatApplicationMachineSendType;
  apiStatus: NiatApplicationMachineReturnType['apiStatus'] &
    NiatPortalGraphQlApiStatusType;
  apiErrors: NiatApplicationMachineReturnType['apiErrors'];
}

export const withNiatApplicationPortalMachine = (
  WrappedComponent: ComponentType<WithNiatApplicationPortalMachineProps>
) => {
  const WithNiatApplicationPortalMachine = (): React.ReactElement | null => {
    const niatMachine = useNiatApplicationMachine();

    const {
      niatApplicationMachineState,
      niatApplicationMachineSend,
      apiStatus,
      apiErrors
    }: NiatApplicationMachineReturnType = niatMachine;

    const {
      context: { graphQlApiStatus }
    } = niatApplicationMachineState;

    const mergedApiStatus = useMemo(
      () => ({
        ...apiStatus,
        ...graphQlApiStatus
      }),
      [apiStatus, graphQlApiStatus]
    );

    return (
      <NiatApplicationMachineProvider
        value={{
          niatApplicationMachineState,
          niatApplicationMachineSend,
          apiStatus: mergedApiStatus,
          apiErrors
        }}
      >
        <WrappedComponent
          niatApplicationMachineState={niatApplicationMachineState}
          niatApplicationMachineSend={niatApplicationMachineSend}
          apiStatus={mergedApiStatus}
          apiErrors={apiErrors}
        />
      </NiatApplicationMachineProvider>
    );
  };

  return React.memo(WithNiatApplicationPortalMachine);
};
